/**
 * TypeScript shapes mirroring the FastAPI response models in
 * `packages/api/pencilout/api/routers/*.py`. When the backend
 * Pydantic models change, update these too — there's no codegen yet.
 */

// ---- Auth ----

/** Returned by /auth/me + /auth/magic-link/consume. */
export interface CurrentUserResponse {
  id: string;
  /** Null for phone-only users — the DB CHECK constraint guarantees
   *  ``email`` and ``phone_number`` aren't both null. */
  email: string | null;
  /** E.164 phone number, set for users who signed up via SMS-OTP
   *  (or added a phone after the fact). Null for email-only users. */
  phone_number: string | null;
  /** True when the user has completed the SMS OTP verify at least
   *  once. Drives "phone verified" badge UI; doesn't gate auth. */
  phone_verified: boolean | null;
  display_name: string;
  role: UserRole;
  status: UserStatus;
  organization_id: string | null;
  organization_name: string | null;
  /** True when the user's last-accepted Terms version is older than
   *  the current ``CURRENT_TERMS_VERSION``. Client should surface a
   *  re-accept modal and POST /auth/accept-terms with the current
   *  version to clear it. Doesn't block API access; soft prompt. */
  terms_accept_required?: boolean;
  /** Set when the current session was opened by a pencilout_admin
   *  "Sign in as" action. UI shows a banner + Stop button. */
  impersonated_by: ImpersonatorRef | null;
}

export interface ImpersonatorRef {
  id: string;
  email: string | null;
  display_name: string;
}

export interface ImpersonateResponse {
  session_token: string;
  expires_at: string;
  target_user_id: string;
  target_email: string;
  target_display_name: string;
}

export type UserRole = "pencilout_admin" | "org_admin" | "org_member";
export type UserStatus = "invited" | "active" | "disabled";

export interface MagicLinkRequestBody {
  email: string;
}

export interface SignupRequest {
  /** At least one of email / phone_number is required server-side. */
  email?: string;
  phone_number?: string;
  display_name: string;
  company_name: string;
  terms_accepted_version: string;
}

export interface SignupResponse {
  message: string;
}

export interface MagicLinkConsumeBody {
  token: string;
}

export interface ConsumeMagicLinkResponse {
  user: CurrentUserResponse;
  session_token: string;
  expires_at: string;
}

// ---- Phone-OTP sign-in ----

export interface PhoneStartBody {
  phone_number: string;
}

export interface PhoneVerifyBody {
  phone_number: string;
  code: string;
}

// ---- Onboarding checklist ----

/** One step in the post-signup setup checklist. */
export interface OnboardingStep {
  /** Stable identifier — switch on for icon / routing decisions. */
  key: string;
  /** Short label rendered as the checklist row title. */
  label: string;
  /** One-line explanation; shown as sub-text. */
  description: string;
  /** Server-computed: true when the step's condition is met. */
  completed: boolean;
  /** Optional in-app route to link the row to. ``null`` for steps
   *  that don't have a single page (e.g. "install the iOS app"). */
  cta_path: string | null;
}

export interface OnboardingState {
  steps: OnboardingStep[];
  completed_count: number;
  total_count: number;
  /** 0.0 - 1.0. Convenience for clients that want a single number. */
  percent_complete: number;
  /** Hint to the UI: render the checklist or hide it. False on
   *  brand-new orgs that haven't done anything yet (no job, no
   *  client, no walkthrough, no sent quote) — so first-load isn't
   *  cluttered with "do these 7 things" before the user has even
   *  poked the product. Flips true on first concrete action. */
  should_render: boolean;
}

// ---- Users ----

export interface UserResponse {
  id: string;
  /** Nullable for phone-only users. */
  email: string | null;
  /** E.164 when set; nullable for email-only users. */
  phone_number?: string | null;
  /** True after a successful SMS-OTP verify. */
  phone_verified?: boolean;
  display_name: string;
  role: UserRole;
  status: UserStatus;
  organization_id: string | null;
  organization_name: string | null;
  created_at: string;
  last_login_at: string | null;
}

export interface InviteUserRequest {
  /** At least one of email / phone_number is required. */
  email?: string;
  phone_number?: string;
  display_name: string;
  role: UserRole;
  organization_id?: string | null;
}

export interface UpdateUserRequest {
  display_name?: string;
  role?: UserRole;
  status?: UserStatus;
  /** pencilout_admin only — escape hatch for fixing typos / address
   *  changes pre-launch. Self-service path (with verification) lives
   *  at POST /me/email/request-change. */
  email?: string;
  /** pencilout_admin only — see ``email`` note. Setting this clears
   *  ``phone_verified`` until the user re-OTPs. */
  phone_number?: string;
}

// ---- Organizations ----

export type OrganizationStatus = "trial" | "active" | "suspended";

/** One row in `Organization.payment_terms_rules`. The rule applies
 *  when the quote total falls within `[min_total_cents, max_total_cents]`
 *  (either bound nullable for open-ended ranges). The schedule is
 *  rendered as a deposit + draws preview on the customer-facing
 *  quote when populated. */
export interface OrgPaymentTermsRule {
  min_total_cents: number | null;
  max_total_cents: number | null;
  schedule: Array<{
    label: string;
    /** Decimal 0–1 (`0.5` = 50%). */
    percent: number;
    /** Free-form trigger description ("on signing", "on rough-in
     *  complete", "on completion", etc.). */
    trigger: string;
  }>;
}

/** One row in `Organization.brand_preferences`. AI generation
 *  autofills the brand into matching category descriptions. */
export interface OrgBrandPreference {
  category: string;
  brand: string;
  model: string | null;
  tier: "budget" | "mid" | "premium" | null;
  /** When true, descriptions get "(or comparable)" appended. */
  or_comparable: boolean;
}

/** One row in `Organization.default_disclaimers`. Renders in a
 *  'Terms & Disclosures' block on the public view + PDF. */
export interface OrgDisclaimer {
  /** Stable key (e.g. "discovery_items", "material_volatility"). */
  key: string;
  title: string;
  body: string;
  /** When false, the entry stays on file but doesn't reach new
   *  quotes — toggle without deleting. */
  enabled: boolean;
}

/** One row in `Organization.payment_methods`. Renders on the public
 *  invoice in a "Payment methods" block. */
export interface OrgPaymentMethod {
  method: string;
  payable_to_or_handle: string | null;
  notes: string | null;
}

/** A change order projected to the homeowner-facing surface.
 *  Drafts are never included in the public payload. */
export interface PublicChangeOrder {
  id: string;
  number: number;
  title: string;
  description: string | null;
  /** Signed cents. Positive = additional work, negative = credit. */
  amount_cents: number;
  status: "sent" | "accepted" | "declined";
  sent_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  signed_name: string | null;
  decline_reason: string | null;
}

/** One entry in a frozen payment-schedule snapshot. Resolved on
 *  quote acceptance from the org's `payment_terms_rules` and the
 *  quote total. The `amount_cents` is the materialized dollar
 *  amount (so the homeowner can see it without recalculating). */
export interface PaymentScheduleEntry {
  label: string;
  /** Decimal 0–1 (`0.5` = 50%). */
  percent: number;
  amount_cents: number;
  trigger: string | null;
}

export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
  status: OrganizationStatus;
  user_count: number;
  job_count: number;
  /** PencilOut's markup over Gemini pass-through (0.20 = 20%). Only
   *  meaningful to pencilout_admin; org users never see this column
   *  in the API but the type stays here since the org endpoints
   *  themselves are admin-gated. */
  ai_markup_pct: number;
  billing_plan: BillingPlan;
  has_logo: boolean;
  /** Optional one-liner shown under the company name on quote PDFs. */
  quote_header_tagline: string | null;
  /** Optional boilerplate seeded into a quote's `terms` field on creation. */
  default_quote_terms: string | null;
  /** Optional deposit pct rendered on the PDF (`0.30` = 30%). NULL = no row. */
  default_deposit_pct: number | null;
  /** Org-level default for new quotes. `itemized` shows the line-item
   *  table on customer-facing surfaces; `artisan` hides the line items
   *  and shows narrative + total only. Per-quote toggle still
   *  overrides on the editor. Default `"itemized"`. */
  default_render_mode: QuoteRenderMode;
  /** Org-level default for the per-quote
   *  `use_sectional_template` toggle. */
  use_sectional_template_default: boolean;
  /** Threshold-based payment-schedule rules. `null` = none. */
  payment_terms_rules: OrgPaymentTermsRule[] | null;
  /** Per-category preferred-brand defaults. AI generation uses
   *  these for line descriptions ("Moen Tetra (or comparable)"). */
  brand_preferences: OrgBrandPreference[] | null;
  /** Boilerplate disclaimers appended to every new quote. */
  default_disclaimers: OrgDisclaimer[] | null;
  /** Accepted payment rails on invoices. */
  payment_methods: OrgPaymentMethod[] | null;
  /** Days between auto-reminder emails for unaccepted sent quotes.
   *  `0` = reminders disabled. */
  quote_reminder_cadence_days: number;
  /** Total cap on reminders the cron will send per quote. `0` disables. */
  quote_reminder_max: number;
  business_phone: string | null;
  business_email: string | null;
  business_website: string | null;
  business_address: string | null;
  /** `#RRGGBB`. NULL = use the house pencil-yellow accent on PDFs. */
  brand_primary_color: string | null;
  /** Mustache-lite override for the quote-send subject line. */
  quote_email_subject_template: string | null;
  quote_email_body_template: string | null;
  /** ISO-639-1 two-letter code (`"en"`, `"es"`, etc.) controlling
   *  the language of AI-generated quote line items, scope summaries,
   *  and terms — the homeowner-facing copy. Walkthrough audio is
   *  transcribed verbatim regardless. Default `"en"`. */
  output_language: string;
  /** Current plan tier. Drives the admin Comp toggle visibility and
   *  the contractor PlanBillingPage view-state.
   *  - `"free"` / `"pro"` / `"crew"`: regular tiers
   *  - `"comped"`: Veridon-internal pilot — no quota, no charges,
   *    never appears as an upgrade/purchase option
   *  - `null`: legacy org that hasn't been migrated to plan_tier */
  plan_tier: string | null;
  created_at: string;
  updated_at: string;
}

export type OrganizationDetail = OrganizationSummary;

export interface UpdateOrgBrandingRequest {
  /** `null` (or empty string server-side) clears the field. Field absent
   *  from the body leaves the stored value untouched (PATCH semantics). */
  quote_header_tagline?: string | null;
  default_quote_terms?: string | null;
  default_deposit_pct?: number | null;
  /** Server pattern-validates `^(itemized|artisan)$`. Sending `null`
   *  is a no-op (PATCH semantics — server ignores nulls on this
   *  field rather than clearing). Omit the field to leave it
   *  untouched. */
  default_render_mode?: QuoteRenderMode | null;
  /** Org-level default for the per-quote sectional-template toggle. */
  use_sectional_template_default?: boolean | null;
  /** Replace the org's payment-terms rules. Send `[]` to clear. */
  payment_terms_rules?: OrgPaymentTermsRule[] | null;
  /** Replace the org's brand defaults. Send `[]` to clear. */
  brand_preferences?: OrgBrandPreference[] | null;
  /** Replace the org's default disclaimers. Send `[]` to clear. */
  default_disclaimers?: OrgDisclaimer[] | null;
  /** Replace the org's accepted payment methods. Send `[]` to clear. */
  payment_methods?: OrgPaymentMethod[] | null;
  quote_reminder_cadence_days?: number;
  quote_reminder_max?: number;
  business_phone?: string | null;
  business_email?: string | null;
  business_website?: string | null;
  business_address?: string | null;
  brand_primary_color?: string | null;
  quote_email_subject_template?: string | null;
  quote_email_body_template?: string | null;
  /** ISO-639-1 two-letter code. Server pattern-validates `^[a-z]{2}$`. */
  output_language?: string | null;
}

// ---- Job templates ----

/** One row in a JobTemplate's `default_line_items` array. Same shape
 *  as a real LineItem minus the per-quote tracking fields (ordinal,
 *  ai_notes, confidence). */
export interface TemplateLineItem {
  category: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price_cents: number;
  visible_to_client: boolean;
}

export interface JobTemplateResponse {
  id: string;
  organization_id: string;
  name: string;
  default_title: string | null;
  default_description: string | null;
  default_line_items: TemplateLineItem[];
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface CreateJobTemplateRequest {
  name: string;
  default_title?: string | null;
  default_description?: string | null;
  default_line_items?: TemplateLineItem[];
}

export interface UpdateJobTemplateRequest {
  name?: string;
  default_title?: string | null;
  default_description?: string | null;
  default_line_items?: TemplateLineItem[];
}

export interface ApplyTemplateToQuoteRequest {
  template_id: string;
  /** When true, drop existing ``source==template`` rows on the
   *  quote before adding new ones. AI + manual rows always survive. */
  replace_existing?: boolean;
}

export interface ApplyTemplateToQuoteResponse {
  quote: QuoteResponse;
  items_added: number;
  items_replaced: number;
  items_preserved: number;
}

export interface CreateOrgRequest {
  name: string;
  slug?: string;
  admin_email: string;
  admin_display_name: string;
}

export interface UpdateOrgRequest {
  name?: string;
  slug?: string;
  status?: OrganizationStatus;
  ai_markup_pct?: number;
  billing_plan?: BillingPlan;
}

// ---- Clients ----

export interface ClientAddressFields {
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string | null;
}

export interface ClientResponse extends ClientAddressFields {
  id: string;
  organization_id: string;
  organization_name: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  notes: string | null;
  job_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateClientRequest extends Partial<ClientAddressFields> {
  organization_id?: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

export interface UpdateClientRequest extends Partial<ClientAddressFields> {
  name?: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

/** Rollup stats returned by `GET /clients/{id}/summary`. */
export interface ClientSummaryResponse {
  total_jobs: number;
  /** Jobs in `open` or `quoted` status. */
  active_jobs: number;
  total_quotes: number;
  /** Map of QuoteStatus value → count. Missing keys = zero. */
  quotes_by_status: Record<string, number>;
  /** Sum of `total_cents` for accepted quotes. */
  accepted_value_cents: number;
  /** Sum of `total_cents` for draft / ready / sent quotes. */
  pipeline_value_cents: number;
  /** Most recent `quote.updated_at` across the client's quotes. */
  last_quote_at: string | null;
  /** Most recent client-side touch (comment, view, accept, decline). */
  last_contact_at: string | null;
}

// ---- Subcontractors ----

export interface SubcontractorResponse {
  id: string;
  organization_id: string;
  organization_name: string | null;
  name: string;
  trade: string | null;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string | null;
  license_number: string | null;
  insurance_expires_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateSubcontractorRequest {
  organization_id?: string;
  name: string;
  trade?: string | null;
  email?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  region?: string | null;
  postal_code?: string | null;
  country?: string | null;
  license_number?: string | null;
  insurance_expires_at?: string | null;
  notes?: string | null;
}

export type UpdateSubcontractorRequest = Partial<
  Omit<CreateSubcontractorRequest, "organization_id">
>;

// ---- Jobs ----

export type JobStatusValue =
  | "open"
  | "quoted"
  | "won"
  | "in_progress"
  | "complete"
  | "lost"
  | "archived";

export interface JobSummary {
  id: string;
  organization_id: string;
  organization_name: string;
  client_id: string | null;
  client_name: string | null;
  creator_id: string;
  creator_display_name: string;
  title: string;
  status: JobStatusValue;
  walkthrough_count: number;
  /** Multi-line work-site address. NULL inherits from the linked client. */
  site_address: string | null;
  /** Planned start / visit (ISO 8601 timestamp). NULL = unscheduled. */
  scheduled_for: string | null;
  schedule_notes: string | null;
  /** Auto-stamped on JobStatus transition to in_progress / complete. */
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobDetail extends JobSummary {
  description: string | null;
}

export interface CreateJobRequest {
  title: string;
  description?: string | null;
  client_id?: string | null;
  /** Required when caller is pencilout_admin. */
  organization_id?: string;
  /** Optional explicit creator. Defaults to caller server-side. */
  creator_id?: string;
  /** Optional JobTemplate this job is being spawned from. Server
   *  records it on `Job.source_template_id` so a subsequent
   *  ``POST /quotes`` (blank quote) seeds line items from the
   *  template's `default_line_items`. */
  template_id?: string;
  site_address?: string | null;
  scheduled_for?: string | null;
  schedule_notes?: string | null;
}

export interface UpdateJobRequest {
  title?: string;
  description?: string | null;
  status?: JobStatusValue;
  client_id?: string | null;
  site_address?: string | null;
  scheduled_for?: string | null;
  schedule_notes?: string | null;
}

// ---- Pricing rules ----

export type PricingTier = "builder" | "mid" | "premium" | "luxury";

export interface PricingRuleResponse {
  id: string;
  organization_id: string;
  organization_name: string | null;
  category: string;
  tier: PricingTier;
  unit: string;
  baseline_cents: number;
  /** Material-side cost component when the rule has a split. NULL =
   *  pure-labor or legacy single-price rule. */
  material_cents: number | null;
  /** Labor-side cost component. Paired with `material_cents`. */
  labor_cents: number | null;
  multiplier: number;
  markup_pct: number;
  notes: string | null;
  effective_from: string;
  effective_until: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePricingRuleRequest {
  organization_id?: string;
  category: string;
  tier: PricingTier;
  unit: string;
  baseline_cents: number;
  /** Optional material/labor split. If supplied, both must be set
   *  and they must sum to `baseline_cents` (server-side check). */
  material_cents?: number | null;
  labor_cents?: number | null;
  multiplier: number;
  markup_pct: number;
  notes?: string | null;
}

export interface UpdatePricingRuleRequest {
  category?: string;
  tier?: PricingTier;
  unit?: string;
  baseline_cents?: number;
  /** Update the material side. Send `-1` to clear the split entirely
   *  (drops back to a legacy single-price rule). */
  material_cents?: number | null;
  /** Update the labor side. Send `-1` to clear the split. */
  labor_cents?: number | null;
  multiplier?: number;
  markup_pct?: number;
  notes?: string | null;
  effective_until?: string | null;
}

// ---- Quotes ----

export type QuoteStatusValue = "draft" | "ready" | "sent" | "accepted" | "declined" | "expired";

/** How a quote renders to the homeowner. `itemized` (legacy default)
 *  shows the full line-item table on customer-facing surfaces;
 *  `artisan` hides the line items and shows project description +
 *  grand total only — what experienced residential remodelers ship
 *  to clients. Per-quote toggle on the editor; org-level default
 *  via `OrganizationSummary.default_render_mode`. */
export type QuoteRenderMode = "itemized" | "artisan";

/** What kind of line item this is — drives badging on the public
 *  view + whether the line counts toward the total.
 *
 *  - `firm` (legacy default): fixed-price line, counts toward total.
 *  - `allowance`: budget reservation for a not-yet-final selection.
 *    Counts at the allowance amount; badged "Allowance".
 *  - `client_supplied`: homeowner provides materials; we charge labor
 *    only. Badged "Client-supplied".
 *  - `tbd`: placeholder, no committed price. Renders as "Pending"
 *    with no price; does NOT count toward the total. */
export type LineItemKind = "firm" | "allowance" | "client_supplied" | "tbd";

/** Position in the canonical 18-section A–R template — the structural
 *  scope skeleton dad uses on every customer-facing room. Letters
 *  preserved in the value so the renderer can show "A. Site
 *  preparation & containment" exactly the way the source document
 *  reads. */
export type LineItemSection =
  | "a"
  | "b"
  | "c"
  | "d"
  | "e"
  | "f"
  | "g"
  | "h"
  | "i"
  | "j"
  | "k"
  | "l"
  | "m"
  | "n"
  | "o"
  | "p"
  | "q"
  | "r";

/** Display label for each section, source of truth for the public
 *  view header text. Keep this string-identical with the server-side
 *  ``LINE_ITEM_SECTION_LABELS`` map so the contractor sees the same
 *  thing the homeowner does. */
export const LINE_ITEM_SECTION_LABELS: Record<LineItemSection, string> = {
  a: "A. Site preparation & containment",
  b: "B. Demolition",
  c: "C. Drywall / framing",
  d: "D. Plumbing rough-in",
  e: "E. Plumbing finish",
  f: "F. Electrical rough-in",
  g: "G. Electrical finish",
  h: "H. HVAC",
  i: "I. Tile / flooring",
  j: "J. Cabinetry",
  k: "K. Countertops",
  l: "L. Backsplash",
  m: "M. Paint / finish",
  n: "N. Fixtures / hardware",
  o: "O. Appliances",
  p: "P. Trim / millwork",
  q: "Q. Final clean",
  r: "R. Construction waste removal",
};

/** Ordered list — used to iterate sections in canonical order when
 *  rendering "no action required" placeholders for empty sections. */
export const LINE_ITEM_SECTION_ORDER: ReadonlyArray<LineItemSection> = [
  "a",
  "b",
  "c",
  "d",
  "e",
  "f",
  "g",
  "h",
  "i",
  "j",
  "k",
  "l",
  "m",
  "n",
  "o",
  "p",
  "q",
  "r",
];

export type LineItemConfidence = "high" | "medium" | "low";

export interface LineItemResponse {
  id: string;
  ordinal: number;
  category: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price_cents: number;
  total_cents: number;
  confidence: LineItemConfidence;
  /** `firm` (default), `allowance`, `client_supplied`, `tbd`. */
  kind: LineItemKind;
  /** Per-line protective text (e.g. "Assumes existing subfloor
   *  structurally sound"). Renders as a callout under the
   *  description on the customer-facing surface. NULL = none. */
  assumptions: string | null;
  /** Free-form room label (Master Bath, Kitchen, etc.). NULL =
   *  unscoped. */
  room: string | null;
  /** Letter `a`–`r` from the canonical 18-section template. NULL =
   *  not assigned. */
  section_code: LineItemSection | null;
  /** Verbatim quote from the walkthrough that triggered this line.
   *  NULL = template / pricing-rule line with no transcript anchor. */
  source_excerpt?: string | null;
  /** Walkthrough video offset (seconds). NULL when not anchored to
   *  a specific moment. */
  source_timestamp_seconds?: number | null;
  visible_to_client: boolean;
  ai_notes: string | null;
  contractor_edited: boolean;
}

export interface QuoteResponse {
  id: string;
  job_id: string;
  walkthrough_id: string | null;
  status: QuoteStatusValue;
  /** `itemized` (legacy, default) shows the full line-item table on
   *  customer-facing surfaces; `artisan` hides the line items and
   *  shows the project description + grand total only. The internal
   *  contractor view (this admin page) always renders the full
   *  breakdown regardless. */
  render_mode: QuoteRenderMode;
  /** When true, the customer-facing render groups line items by the
   *  canonical 18-section A–R template + shows "no action required"
   *  placeholders for empty sections. */
  use_sectional_template: boolean;
  /** Frozen payment schedule resolved on accept. NULL until accept
   *  OR when no org rule matched the total. */
  payment_schedule_snapshot: PaymentScheduleEntry[] | null;
  /** Linked-client denormalization — non-null when the parent Job
   *  has a Client. Lets the right-rail client card render without
   *  an extra fetch. */
  client_id?: string | null;
  client_name?: string | null;
  client_email?: string | null;
  total_cents: number;
  markup_pct: number;
  terms: string | null;
  valid_until: string | null;
  sent_at: string | null;
  last_sent_at: string | null;
  view_count: number;
  client_viewed_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  signed_name: string | null;
  signed_ip: string | null;
  signed_user_agent: string | null;
  decline_reason: string | null;
  has_share_link: boolean;
  /** Deposit tracking (post-acceptance, off-platform receipt). */
  deposit_amount_cents: number | null;
  deposit_received_at: string | null;
  deposit_note: string | null;
  created_at: string;
  updated_at: string;
  line_items: LineItemResponse[];
}

export interface SendQuoteRequest {
  recipient_override?: string | null;
  custom_message?: string | null;
}

export interface SendQuoteResponse {
  quote: QuoteResponse;
  share_url: string;
}

/** Returned by ``POST /quotes/{id}/share-link``. The contractor
 *  uses ``share_url`` however they want — typically dropped into
 *  iMessage via an ``sms:`` deep-link with ``suggested_message``
 *  pre-loaded so the homeowner gets the link from the
 *  contractor's own phone number, not a Twilio long code. */
export interface MintShareLinkResponse {
  share_url: string;
  suggested_message: string;
}

// ---- Public quote view (no auth) ----

export interface PublicQuoteLineItem {
  ordinal: number;
  category: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price_cents: number;
  total_cents: number;
  /** `firm` (default), `allowance`, `client_supplied`, `tbd`.
   *  Older API clients may omit this; default-render as `firm`. */
  kind?: LineItemKind;
  /** Per-line protective text. NULL/missing = no callout. */
  assumptions?: string | null;
  /** Free-form room label. */
  room?: string | null;
  /** Letter `a`–`r` from the 18-section template. */
  section_code?: LineItemSection | null;
}

export interface PublicQuoteContractor {
  name: string;
  sender_name: string | null;
  /** Path on the API for the org's logo (e.g. ``/organizations/{id}/logo``).
   *  Compose with ``apiBase`` to render. NULL when no logo uploaded. */
  logo_url: string | null;
}

export interface PublicQuoteClient {
  name: string;
  email: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string | null;
}

export interface PublicQuoteResponse {
  id: string;
  status: QuoteStatusValue;
  /** `itemized` (default) renders the line-items table on the public
   *  view + PDF. `artisan` hides line items and shows narrative +
   *  grand total only. Server still ships the line_items array under
   *  artisan mode so older clients (e.g. iOS that hasn't learned the
   *  toggle) degrade to the legacy view, the safer fallback. */
  render_mode: QuoteRenderMode;
  /** When true, the customer-facing render groups line items by the
   *  canonical 18-section A–R template. */
  use_sectional_template?: boolean;
  job_title: string;
  job_description: string | null;
  contractor: PublicQuoteContractor;
  client: PublicQuoteClient | null;
  line_items: PublicQuoteLineItem[];
  subtotal_cents: number;
  markup_pct: number;
  total_cents: number;
  terms: string | null;
  valid_until: string | null;
  sent_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  signed_name: string | null;
  decline_reason: string | null;
  /** Org-level disclaimers (only `enabled=true` entries). NULL =
   *  none configured. */
  org_disclaimers?: OrgDisclaimer[] | null;
  /** Org-level accepted payment methods. NULL = none configured. */
  org_payment_methods?: OrgPaymentMethod[] | null;
  /** Frozen payment-schedule snapshot resolved on accept. NULL
   *  until accept OR when no org rule matched the total. */
  payment_schedule?: PaymentScheduleEntry[] | null;
  /** Change orders the homeowner can see (sent / accepted /
   *  declined). Drafts are never exposed. Empty when no CO has
   *  reached the homeowner. */
  change_orders?: PublicChangeOrder[];
  /** Customer-portal extension — set once the contractor records
   *  a deposit receipt (off-platform). NULL = none received yet. */
  deposit_amount_cents: number | null;
  deposit_received_at: string | null;
  /** Mirrors parent Job.status when the project is past sales
   *  (in_progress / complete). NULL while still in the funnel. */
  project_status: "in_progress" | "complete" | null;
  project_started_at: string | null;
  project_completed_at: string | null;
  project_scheduled_for: string | null;
}

export interface LineItemPayload {
  id?: string | null;
  category: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price_cents: number;
  confidence: LineItemConfidence;
  /** Default `firm` server-side; sending undefined leaves
   *  legacy behavior intact. */
  kind?: LineItemKind;
  /** Optional per-line assumption text. `null` clears. */
  assumptions?: string | null;
  /** Optional free-form room label. */
  room?: string | null;
  /** Optional canonical-section letter (`a`–`r`). */
  section_code?: LineItemSection | null;
  /** AI-recorded provenance — round-tripped on PATCH so the
   *  walkthrough-jump link survives an editor save. Contractor
   *  doesn't normally touch these directly. */
  source_excerpt?: string | null;
  source_timestamp_seconds?: number | null;
  visible_to_client: boolean;
  ai_notes?: string | null;
}

// ---- Change orders + project payments (post-acceptance ledger) ----

export type ChangeOrderStatus = "draft" | "sent" | "accepted" | "declined";

export interface ChangeOrderResponse {
  id: string;
  quote_id: string;
  /** Sequential 1-indexed number per quote (CO #1, CO #2, etc.). */
  number: number;
  title: string;
  description: string | null;
  /** Signed cents. Positive = additional work; negative = credit. */
  amount_cents: number;
  status: ChangeOrderStatus;
  sent_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  signed_name: string | null;
  decline_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateChangeOrderRequest {
  title: string;
  description?: string | null;
  /** Signed; zero allowed for scope-only changes. */
  amount_cents: number;
}

export interface UpdateChangeOrderRequest {
  title?: string;
  description?: string | null;
  amount_cents?: number;
  /** Only `draft | sent` accepted via PATCH; accept/decline are
   *  driven through the public sign endpoints. */
  status?: "draft" | "sent";
}

export type PaymentMethod =
  | "check"
  | "ach"
  | "cash"
  | "zelle"
  | "venmo"
  | "wire"
  | "cc"
  | "other"
  | string; // string fallback so adding a new method doesn't break the type

export interface ProjectPaymentResponse {
  id: string;
  quote_id: string;
  payment_number: number;
  received_at: string;
  method: PaymentMethod;
  /** Signed cents. Positive = received; negative = refund/chargeback. */
  amount_cents: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePaymentRequest {
  amount_cents: number;
  method?: PaymentMethod;
  /** Defaults to now when omitted. ISO 8601 string. */
  received_at?: string | null;
  notes?: string | null;
}

export interface UpdatePaymentRequest {
  amount_cents?: number;
  method?: PaymentMethod;
  received_at?: string | null;
  notes?: string | null;
}

export interface UpdateQuoteRequest {
  status?: QuoteStatusValue;
  /** `itemized` or `artisan`. Server validates against the enum;
   *  omit to leave the existing mode untouched (PATCH semantics). */
  render_mode?: QuoteRenderMode;
  /** Per-quote toggle for the 18-section A–R sectional template.
   *  Omit to leave unchanged. */
  use_sectional_template?: boolean;
  markup_pct?: number;
  terms?: string | null;
  valid_until?: string | null;
  line_items?: LineItemPayload[];
}

export interface LineItemSuggestion {
  category: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price_cents: number;
  confidence: LineItemConfidence;
  ai_notes: string;
  source_excerpt: string;
}

export interface SuggestionsResponse {
  suggestions: LineItemSuggestion[];
}

// ---- Quote comments ----

export type QuoteCommentAuthorType = "client" | "contractor";

/** Attachment metadata exposed on a public-quote comment.
 *  ``download_url`` is API-relative (``/q/{token}/comments/{cid}/attachments/{aid}``);
 *  prepend ``apiBase`` to render in an ``<img>`` or ``<a>``. */
export interface PublicQuoteCommentAttachment {
  id: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
  download_url: string;
}

/** Same shape as :type:`PublicQuoteCommentAttachment` but reachable
 *  via the contractor route (``/quotes/{q}/comments/{c}/attachments/{a}``).
 *  Kept as a distinct named type so call sites that need the
 *  contractor surface can be grepped explicitly. */
export type QuoteCommentAttachmentResponse = PublicQuoteCommentAttachment;

export interface QuoteCommentResponse {
  id: string;
  author_type: QuoteCommentAuthorType;
  author_name: string;
  body: string;
  parent_comment_id: string | null;
  created_at: string;
  /** Files attached to the comment. Defaults ``[]`` so older API
   *  responses without the field still parse cleanly. */
  attachments?: QuoteCommentAttachmentResponse[];
}

/** Curated, contractor-managed attachment hung directly off a Job.
 *  Created either by direct upload (``source = "direct_upload"``) or
 *  promoted from a quote-comment attachment
 *  (``source = "promoted_from_comment"``); the latter retains a
 *  pointer to its origin in ``promoted_from_comment_attachment_id``. */
export interface JobAttachmentResponse {
  id: string;
  job_id: string;
  label: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  source: "direct_upload" | "promoted_from_comment";
  promoted_from_comment_attachment_id: string | null;
  uploaded_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  /** API-relative — prepend ``apiBase`` to fetch. Auth via session
   *  cookie; the GET re-checks org access against the parent job. */
  download_url: string;
}

export interface InboxItem {
  quote_id: string;
  job_id: string;
  job_title: string;
  organization_id: string;
  organization_name: string;
  client_name: string | null;
  unanswered_count: number;
  latest_comment_excerpt: string;
  latest_comment_at: string;
  latest_comment_author: string;
}

// ---- Usage / billing ----

export type UsageOperation = "walkthrough_transcribe" | "quote_generate" | "quote_suggest";

export interface UsageRollupRow {
  organization_id: string;
  organization_name: string;
  /** ISO YYYY-MM. */
  year_month: string;
  operation: string;
  call_count: number;
  input_tokens: number;
  output_tokens: number;

  /** What we'd invoice the org for this row. Always present.
   *  For org users this is the only number visible. Penny-accurate
   *  at this row's boundary — NEVER sum across rows; sum
   *  ``billed_micros`` and re-ceil. */
  billed_cents: number;
  billed_micros: number;

  /** Pencilout-admin only. Pass-through Gemini cost before markup. */
  raw_cost_cents: number | null;
  raw_cost_micros: number | null;
  /** Pencilout-admin only. The org's markup at response time. */
  markup_pct: number | null;
}

export interface UsageSummaryResponse {
  rows: UsageRollupRow[];
  grand_total_billed_cents: number;
  grand_total_billed_micros: number;
  /** Admin-only fields. */
  grand_total_raw_cents: number | null;
  grand_total_raw_micros: number | null;
}

export interface UsageEventResponse {
  id: string;
  created_at: string;
  organization_id: string;
  organization_name: string;
  operation: string;
  walkthrough_id: string | null;
  job_id: string | null;
  quote_id: string | null;
  request_id: string;
  provider: string;
  model: string;
  pricing_version: string;
  input_micros_per_token: number;
  output_micros_per_token: number;
  input_tokens: number;
  output_tokens: number;
  duration_ms: number | null;
  succeeded: boolean;
  error_message: string | null;

  billed_cents: number;
  billed_micros: number;
  raw_cost_cents: number | null;
  raw_cost_micros: number | null;
  markup_pct: number | null;
}

export interface UsageEventsResponse {
  events: UsageEventResponse[];
  total: number;
  limit: number;
  offset: number;
}

// ---- Billing ----

export type StripeMode = "test" | "live";

export type BillingPlan = "comped" | "ai_usage_only" | "platform_plus_usage";

export interface AppConfigResponse {
  stripe_mode: StripeMode;
  has_test_api_key: boolean;
  has_live_api_key: boolean;
  stripe_test_api_key_last4: string | null;
  stripe_live_api_key_last4: string | null;
  has_test_webhook_secret: boolean;
  has_live_webhook_secret: boolean;
  stripe_platform_price_id_test: string | null;
  stripe_platform_price_id_live: string | null;

  // AI provider routing
  walkthrough_transcribe_model: string | null;
  quote_generate_model: string | null;
  quote_suggest_model: string | null;
  has_anthropic_api_key: boolean;
  anthropic_api_key_last4: string | null;
}

export interface UpdateAppConfigRequest {
  stripe_mode?: StripeMode;
  /** Plaintext value to encrypt + persist; "" clears; omit to leave unchanged. */
  stripe_test_api_key?: string;
  stripe_live_api_key?: string;
  stripe_test_webhook_secret?: string;
  stripe_live_webhook_secret?: string;
  stripe_platform_price_id_test?: string;
  stripe_platform_price_id_live?: string;

  walkthrough_transcribe_model?: string;
  quote_generate_model?: string;
  quote_suggest_model?: string;
  anthropic_api_key?: string;
}

export interface ModelCatalogEntry {
  key: string;
  model: string;
  input_dollars_per_1m: number;
  output_dollars_per_1m: number;
}

export interface ModelCatalogResponse {
  pricing_version: string;
  last_verified_on: string;
  by_provider: Record<string, ModelCatalogEntry[]>;
}

export interface GenerateInvoiceResponse {
  organization_id: string;
  period_year_month: string;
  line_items: number;
  amount_cents: number;
  stripe_invoice_id: string;
  stripe_mode: StripeMode;
}

export interface BillingEventResponse {
  id: string;
  organization_id: string | null;
  stripe_mode: StripeMode;
  stripe_event_id: string | null;
  event_type: string;
  invoice_id: string | null;
  amount_cents: number | null;
  succeeded: boolean;
  error_message: string | null;
  created_at: string;
}

// ---- Plans (customer-facing pricing tiers) ----

export interface PlanTierResponse {
  tier: string;
  name: string;
  tagline: string;
  price_monthly_cents: number;
  included_walkthroughs: number;
  overage_cents_per_walkthrough: number | null;
  watermark_pdf: boolean;
  is_public: boolean;
  sort_order: number;
  features: string[];
  stripe_test_product_id: string | null;
  stripe_test_price_id_monthly: string | null;
  stripe_test_price_id_overage: string | null;
  stripe_live_product_id: string | null;
  stripe_live_price_id_monthly: string | null;
  stripe_live_price_id_overage: string | null;
  is_synced_test: boolean;
  is_synced_live: boolean;
}

export interface PlanVersionResponse {
  version: string;
  record_id: string;
  effective_from: string;
  note: string | null;
  created_at: string;
  created_by_user_id: string | null;
  tiers: PlanTierResponse[];
}

/** Editable shape for a tier in the admin "Save new version" form.
 *  Stripe IDs are owned by the sync flow, not the editor. */
export interface PlanTierDraft {
  tier: string;
  name: string;
  tagline: string;
  price_monthly_cents: number;
  included_walkthroughs: number;
  overage_cents_per_walkthrough: number | null;
  watermark_pdf: boolean;
  is_public: boolean;
  sort_order: number;
  features: string[];
}

export interface SaveNewPlanVersionRequest {
  note?: string | null;
  tiers: PlanTierDraft[];
}

export interface SaveNewPlanVersionResponse {
  version: string;
}

export interface SyncStripeTierResult {
  tier: string;
  product_id: string | null;
  price_id_monthly: string | null;
  price_id_overage: string | null;
  actions: string[];
}

export interface SyncStripeResponse {
  new_version: string;
  mode: StripeMode;
  tiers: SyncStripeTierResult[];
}

export interface PublicPlanTierResponse {
  tier: string;
  name: string;
  tagline: string;
  price_monthly_cents: number;
  included_walkthroughs: number;
  overage_cents_per_walkthrough: number | null;
  watermark_pdf: boolean;
  sort_order: number;
  features: string[];
}

// ---- Subscription status (org-side, drives the plan & billing UI) ----

export interface SubscriptionStatusResponse {
  organization_id: string;
  /** Tier key from the active plan version (``free`` / ``pro`` / ``crew``).
   *  NULL = legacy org that hasn't been migrated to the plan_tier system. */
  plan_tier: string | null;
  /** Set when a plan change has been staged but not yet applied —
   *  ``pending_plan_change_at`` will hold the effective date. */
  pending_plan_tier: string | null;
  pending_plan_change_at: string | null;
  walkthroughs_used: number;
  walkthroughs_included: number;
  walkthroughs_over: number;
  overage_cents_per_walkthrough: number | null;
  period_start: string;
  period_end: string;
  has_subscription: boolean;
  /** True iff a Stripe metered SubscriptionItem id is on file in the
   *  active mode. False on a paid tier here is a billing-leak signal. */
  has_metered_item: boolean;
  stripe_mode: StripeMode;
}

/** One walkthrough's contribution to the cycle's overage detail. */
export interface OverageWalkthroughItem {
  walkthrough_id: string | null;
  job_title: string | null;
  finalized_at: string;
  /** True iff this row falls beyond the included quota. */
  counts_as_overage: boolean;
}

/** Live per-cycle overage breakdown — visible to org_admin + staff
 *  only. Drives the Overage card on PlanBillingPage. */
export interface OverageDetailResponse {
  organization_id: string;
  period_start: string;
  period_end: string;
  included: number;
  used: number;
  over: number;
  rate_cents_per_walkthrough: number;
  accrued_overage_cents: number;
  walkthroughs: OverageWalkthroughItem[];
}

export interface CheckoutSessionRequest {
  plan_tier: string;
  success_url: string;
  cancel_url: string;
}

export interface CheckoutSessionResponse {
  url: string;
  plan_tier: string;
  stripe_mode: StripeMode;
}

export interface RequestPlanChangeBody {
  plan_tier: string;
}

export interface RequestPlanChangeResponse {
  plan_tier: string;
  effective_at: string;
}

// ---- Pricing drift (admin only) ----

export interface PricingProposalResponse {
  id: string;
  detected_at: string;
  provider: string;
  status: "pending" | "applied" | "dismissed" | string;
  active_pricing_version: string;
  changes_summary: string;
  evidence: string | null;
  confidence: "high" | "medium" | "low" | string;
  source_url: string | null;
  reviewed_by_user_id: string | null;
  reviewed_at: string | null;
  applied_to_pricing_version: string | null;
  dismiss_reason: string | null;
}

export interface PricingDriftProviderResult {
  provider: string;
  status: string;
  proposal_id: string | null;
  confidence: string | null;
  summary_lines: string[];
  error: string | null;
}

export interface PricingDriftCheckResponse {
  active_version: string;
  checked_at: string;
  providers: PricingDriftProviderResult[];
}

export interface PricingVersionRecordResponse {
  id: string;
  version: string;
  effective_from: string;
  last_verified_on: string;
  note: string | null;
  created_at: string;
  created_by_user_id: string | null;
  source_proposal_id: string | null;
  rates_json: string;
}

// ---- Service tokens (admin only) ----

export interface ServiceTokenResponse {
  id: string;
  name: string;
  scope: string;
  acts_as_user_id: string;
  last_4: string;
  created_at: string;
  created_by_user_id: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

// ---- Admin audit ----

/** One row from /admin/audit-events. Append-only audit trail of
 *  admin actions (impersonation, plan changes, billing edits, token
 *  mints, etc.). ``payload_json`` is a JSON string the UI parses for
 *  display — kept as a string here so the type stays stable as the
 *  payload shape evolves per action. */
export interface AdminAuditEventResponse {
  id: string;
  actor_user_id: string | null;
  actor_email: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  payload_json: string | null;
  created_at: string;
}

// ---- Email deliverability events (Resend webhook) ----

/** One row from /admin/email-events. Captured by the Resend webhook
 *  (delivered / bounced / complained / opened / clicked). Used by the
 *  admin "Email events" viewer to answer "did this customer actually
 *  get their magic-link / quote / etc." without leaving PencilOut. */
export interface EmailEventResponse {
  id: string;
  resend_event_id: string;
  event_type: string;
  recipient_email: string | null;
  subject: string | null;
  bounce_type: string | null;
  payload_json: string | null;
  created_at: string;
}

export interface CreateServiceTokenRequest {
  name: string;
  /** ``cron`` for limited cron-endpoint access, ``pencilout_admin`` for
   *  full admin (default). Backend validates against a closed set —
   *  unknown values 422. */
  scope?: "cron" | "pencilout_admin";
  acts_as_user_id?: string | null;
  /** ISO 8601. Must be in the future. NULL = never expires. */
  expires_at?: string | null;
}

export interface CreateServiceTokenResponse {
  token: ServiceTokenResponse;
  /** Returned ONCE on creation. Show, copy, and never get it again. */
  raw_token: string;
}

// ---- Dashboard ----

export type DashboardRangeKey =
  | "this_month"
  | "last_30_days"
  | "last_90_days"
  | "this_quarter"
  | "this_year"
  | "all_time";

export interface DashboardFunnel {
  sent: number;
  /** Subset of sent that have a non-null `client_viewed_at`. */
  viewed: number;
  accepted: number;
  declined: number;
  expired: number;
}

export interface DashboardUserRow {
  user_id: string;
  display_name: string;
  quotes_sent: number;
  quotes_accepted: number;
  accepted_value_cents: number;
}

export interface DashboardMetrics {
  scope: "platform" | "org";
  organization_id: string | null;
  range_key: DashboardRangeKey;
  range_label: string;
  range_start: string | null;
  range_end: string;
  active_jobs: number;
  walkthroughs_last_7_days: number;
  quotes_in_flight: number;
  quotes_sent_in_range: number;
  quotes_accepted_in_range: number;
  quotes_declined_in_range: number;
  ai_billed_cents_in_range: number;
  accepted_quote_value_cents_in_range: number;
  win_rate_pct: number | null;
  funnel: DashboardFunnel;
  per_user: DashboardUserRow[];
  organizations_count: number | null;
  paying_organizations_count: number | null;
  users_count: number | null;
}

// ---- Walkthroughs ----

export type WalkthroughStatusValue =
  | "collecting"
  | "uploading"
  | "uploaded"
  | "processing"
  | "processed"
  | "failed";

export type WalkthroughClipStatusValue = "uploading" | "ready" | "failed";

export interface WalkthroughClipResponse {
  id: string;
  clip_order: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  status: WalkthroughClipStatusValue;
  uploaded_at: string | null;
  created_at: string;
}

export interface WalkthroughResponse {
  id: string;
  job_id: string;
  /** Mirrored from the first clip for back-compat with iOS build-9.
   *  New UI should prefer ``clips[0]``. */
  filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  status: WalkthroughStatusValue;
  uploaded_at: string | null;
  finalized_at: string | null;
  processed_at: string | null;
  created_at: string;
  processing_run_id: number;
  clips: WalkthroughClipResponse[];
  clip_count: number;
  transcript: string | null;
  scope_summary: string | null;
  ai_cost_cents: number | null;
  error_message: string | null;
}

// ---- Webhook endpoints (outbound) ----

export interface WebhookEndpointResponse {
  id: string;
  organization_id: string;
  url: string;
  description: string | null;
  /** Allowlist of event types. Empty array = subscribed to all. */
  event_types: string[];
  enabled: boolean;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  /** Server returns true once a secret is provisioned. The plaintext
   *  secret only comes back on create + rotate-secret responses. */
  secret_set: boolean;
}

/** Returned ONLY by create + rotate-secret. Carries the plaintext
 *  signing key one time so the contractor can paste it into the
 *  recipient's setup; subsequent reads omit it. */
export interface WebhookEndpointWithSecret extends WebhookEndpointResponse {
  signing_secret: string;
}

export interface CreateWebhookEndpointRequest {
  url: string;
  description?: string | null;
  /** Subscribe to a subset of event types. Omit / empty array =
   *  subscribe to all. */
  event_types?: string[] | null;
  organization_id?: string;
}

export interface UpdateWebhookEndpointRequest {
  url?: string;
  description?: string | null;
  /** Replace the allowlist; ``[]`` = subscribe to all. */
  event_types?: string[] | null;
  enabled?: boolean;
}

export interface WebhookDeliveryResponse {
  id: string;
  endpoint_id: string;
  event_type: string;
  response_status: number | null;
  response_body_excerpt: string | null;
  error_message: string | null;
  attempt: number;
  created_at: string;
}
